import { Helmet } from 'react-helmet-async';
import {
	createContext,
	useContext,
	useState,
	useEffect,
	useMemo,
	type Dispatch,
	type SetStateAction,
	type ReactNode,
} from 'react';
import { brand } from '../config/data/Constants';
import { getChromeThemeColor } from '../config/ui/theme';
import { useTheme } from './ThemeContext';

export interface PageTitleContextValue {
	setTitle: Dispatch<SetStateAction<string>>;
	setAppear: Dispatch<SetStateAction<boolean>>;
	setDescription: Dispatch<SetStateAction<string>>;
	setPath: Dispatch<SetStateAction<string>>;
}

interface PageHelmetProps {
	title: string;
	appear: boolean;
	description: string;
	path: string;
}

interface UseTitleOptions {
	title?: string;
	appear?: boolean;
	/** Page-specific meta / Open Graph description. Falls back to site default. */
	description?: string;
	/** Canonical path for og:url (e.g. `/app`). Defaults to site root. */
	path?: string;
}

const PageTitleContext = createContext<PageTitleContextValue | undefined>(undefined);

const siteName = brand.organizationShortName;

const formatDocumentTitle = (title: string): string => {
	const trimmed = title.trim();
	if (
		!trimmed ||
		trimmed === 'Home' ||
		trimmed === siteName ||
		trimmed === brand.organizationShortName
	) {
		return siteName;
	}
	return `${trimmed} | ${siteName}`;
};

const PageHelmet = ({ title, appear, description, path }: PageHelmetProps) => {
	const { darkMode, primaryColor } = useTheme();
	const robots = appear ? 'index, follow' : 'no-index, no-follow';
	const imageUrl = `${brand.url}${brand.ogImage}`;
	const chromeThemeColor = getChromeThemeColor(darkMode, primaryColor);
	const documentTitle = formatDocumentTitle(title);
	const pageUrl = `${brand.url}${path || ''}`;
	const metaDescription = description.trim() || brand.metaDescription;

	return (
		<Helmet>
			{/* Primary Meta Tags */}
			<title>{documentTitle}</title>
			<meta name='description' content={metaDescription} />
			<meta name='keywords' content={brand.keywords} />
			<meta name='robots' content={robots} />
			<meta name='author' content={brand.metaAuthor} />
			<meta name='viewport' content='width=device-width, initial-scale=1' />
			<link rel='canonical' href={pageUrl} />

			{/* Open Graph */}
			<meta property='og:title' content={documentTitle} />
			<meta property='og:description' content={metaDescription} />
			<meta property='og:type' content='website' />
			<meta property='og:url' content={pageUrl} />
			<meta property='og:image' content={imageUrl} />
			<meta property='og:site_name' content={siteName} />
			<meta property='og:locale' content='en_US' />

			{/* Structured Data */}
			<script type='application/ld+json'>
				{`
				{
					"@context": "https://schema.org",
					"@type": "Organization",
					"url": "${brand.url}",
					"logo": "${imageUrl}",
					"name": "${brand.organizationShortName}",
					"contactPoint": {
						"@type": "ContactPoint",
						"telephone": "${brand.contactTelephone}",
						"contactType": "${brand.contactType}"
					}
				}
			`}
			</script>

			{/* Mobile: theme-color tracks page surface; Smart App Banner is Safari-only and dismissible */}
			<meta name='mobile-web-app-capable' content='yes' />
			<meta name='theme-color' content={chromeThemeColor} />
			<meta name='msapplication-TileColor' content={chromeThemeColor} />
			<meta
				name='apple-itunes-app'
				content={`app-id=${brand.appleAppId}, app-argument=${brand.url}/app`}
			/>
			<link rel='apple-touch-icon' href={brand.appleTouchIcon} />
		</Helmet>
	);
};

export const PageTitleProvider = ({ children }: { children: ReactNode }) => {
	const [title, setTitle] = useState<string>(siteName);
	const [appear, setAppear] = useState<boolean>(true);
	const [description, setDescription] = useState<string>(brand.metaDescription);
	const [path, setPath] = useState<string>('');

	const value = useMemo<PageTitleContextValue>(
		() => ({ setTitle, setAppear, setDescription, setPath }),
		[],
	);

	return (
		<PageTitleContext.Provider value={value}>
			<PageHelmet title={title} appear={appear} description={description} path={path} />
			{children}
		</PageTitleContext.Provider>
	);
};

export const useTitle = ({ title, appear, description, path }: UseTitleOptions): void => {
	const context = useContext(PageTitleContext);

	if (!context) {
		throw new Error('useTitle must be used within a PageTitleProvider');
	}

	const { setTitle, setAppear, setDescription, setPath } = context;

	useEffect(() => {
		if (title) {
			setTitle(title);
		}
		if (appear !== undefined) {
			setAppear(appear);
		}
		setDescription(description?.trim() ? description : brand.metaDescription);
		setPath(path ?? '');
	}, [title, appear, description, path, setTitle, setAppear, setDescription, setPath]);
};
